import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
  Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StoreIcon from '@mui/icons-material/Store';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import ImageIcon from '@mui/icons-material/Image';
import CategoryIcon from '@mui/icons-material/Category';
import TuneIcon from '@mui/icons-material/Tune';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PaidIcon from '@mui/icons-material/Paid';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LockIcon from '@mui/icons-material/Lock';
import ChatIcon from '@mui/icons-material/Chat';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { useAuth } from '../services/AuthService';
import AutoPrintListener from './AutoPrintListener';
import SubscriptionPaymentDialog from './SubscriptionPaymentDialog';
import WebPushButton from './WebPushButton';

const drawerWidth = 240;

const getSaoPauloDateKey = (value) => {
  if (!value) return '';
  const text = String(value);

  // Datas puras do banco (YYYY-MM-DD) não devem sofrer conversão de fuso.
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text.slice(0, 10);

  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const daysBetweenDateKeys = (fromKey, toKey) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fromKey || '') || !/^\d{4}-\d{2}-\d{2}$/.test(toKey || '')) return null;
  const [fromYear, fromMonth, fromDay] = fromKey.split('-').map(Number);
  const [toYear, toMonth, toDay] = toKey.split('-').map(Number);
  return Math.round(
    (Date.UTC(toYear, toMonth - 1, toDay) - Date.UTC(fromYear, fromMonth - 1, fromDay)) / 86400000
  );
};

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [dueAlert, setDueAlert] = useState({ open: false, store: null, dueDate: '', reason: 'due' });
  const [subscriptionBlocked, setSubscriptionBlocked] = useState({ blocked: false, store: null, message: '' });
  const [subscriptionPaymentOpen, setSubscriptionPaymentOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, api, isAdminMaster, isAdminLanchonete } = useAuth();
  const adminBasePath = location.pathname.startsWith('/admin') ? '/admin' : '';

  useEffect(() => {
    let active = true;
    const loadPaymentDue = async () => {
      if (!['admin_lanchonete', 'cashier', 'kitchen', 'waiter'].includes(user?.user_type) || !user?.lanchonete_id) return;
      try {
        const response = await api.get(`/lanchonetes/${user.lanchonete_id}`);
        const store = response.data;
        const dueValue = store?.subscription_enforcement?.dueDate || store?.subscription_due_date;
        const dueDate = getSaoPauloDateKey(dueValue);
        const trialEndDate = getSaoPauloDateKey(
          store?.trial_end_date || store?.subscription_enforcement?.trialEndsAt
        );
        const today = getSaoPauloDateKey(new Date());
        const daysUntilDue = daysBetweenDateKeys(today, dueDate);
        const daysUntilTrialEnd = daysBetweenDateKeys(today, trialEndDate);
        const phase = String(store?.subscription_enforcement?.phase || '').toLowerCase();
        const status = String(store?.subscription_status || '').toLowerCase();
        const blocked = store?.subscription_enforcement?.canOperateStore === false
          || store?.subscription_enforcement?.paymentRequired === true
          || ['overdue', 'blocked', 'past_due', 'unpaid'].includes(phase)
          || ['overdue', 'blocked', 'past_due', 'unpaid'].includes(status);
        if (active) {
          setSubscriptionBlocked({
            blocked,
            store,
            message: store?.subscription_enforcement?.message || 'A mensalidade esta vencida.'
          });
        }
        const trialWarning = ['trial', 'trialing'].includes(phase || status)
          && daysUntilTrialEnd !== null && daysUntilTrialEnd >= 0 && daysUntilTrialEnd <= 3;
        const dueWarning = daysUntilDue !== null
          && (daysUntilDue <= 3 || ['grace', 'overdue', 'blocked'].includes(phase));
        if (active && !blocked && (dueWarning || trialWarning) && isAdminLanchonete()) {
          const warningDays = trialWarning ? daysUntilTrialEnd : daysUntilDue;
          setDueAlert({
            open: true,
            store,
            dueDate: trialWarning ? (dueDate || trialEndDate) : dueDate,
            days: warningDays,
            reason: trialWarning
              ? (warningDays === 0 ? 'trial-ending' : 'trial-upcoming')
              : (warningDays < 0 ? 'overdue' : (warningDays === 0 ? 'due' : 'upcoming'))
          });
        }
      } catch (error) {
        console.error('Nao foi possivel consultar o vencimento da mensalidade:', error);
      }
    };
    loadPaymentDue();
    return () => { active = false; };
  }, [api, isAdminLanchonete, user?.lanchonete_id, user?.user_type]);

  const formatDueDate = (value) => {
    if (!value) return '';
    const [year, month, day] = String(value).slice(0, 10).split('-');
    return year && month && day ? `${day}/${month}/${year}` : value;
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  const handleOpenPasswordDialog = () => {
    handleProfileMenuClose();
    setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword) {
      setPasswordError('Preencha todos os campos de senha');
      return;
    }
    if (passwords.newPassword.length < 6) {
      setPasswordError('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordError('A confirmação da senha não confere');
      return;
    }

    try {
      await api.put('/users/password', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });
      setPasswordDialogOpen(false);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Não foi possível alterar a senha');
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Saude do sistema', icon: <HealthAndSafetyIcon />, path: '/operations', lanchoneteOnly: true },
    { text: 'Lanchonetes', icon: <StoreIcon />, path: '/lanchonetes', adminOnly: true },
    { text: 'Assinaturas (Mercado Pago)', icon: <PaidIcon />, path: '/subscriptions', adminOnly: true },
    { text: 'Laboratório Stripe', icon: <CreditCardIcon />, path: '/stripe-test', adminOnly: true },
    { text: 'Minha Lanchonete', icon: <StoreIcon />, path: '/admin-lanchonete', lanchoneteOnly: true },
    { text: 'Produtos', icon: <ShoppingCartIcon />, path: '/produtos' },
    { text: 'Estoque', icon: <Inventory2Icon />, path: '/estoque' },
    { text: 'Categorias', icon: <CategoryIcon />, path: '/categorias' },
    { text: 'Pedidos', icon: <ReceiptIcon />, path: '/pedidos' },
    { text: 'Cozinha', icon: <RestaurantIcon />, path: '/cozinha', lanchoneteOnly: true },
    { text: 'Fechamento de Caixa', icon: <PaidIcon />, path: '/fechamento', lanchoneteOnly: true },
    { text: 'Promoções e Cupons', icon: <LocalOfferIcon />, path: '/promocoes' },
    { text: 'Recursos para Clientes', icon: <TuneIcon />, path: '/customer-features', lanchoneteOnly: true },
    { text: 'Laboratorio WhatsApp', icon: <WhatsAppIcon />, path: '/whatsapp-test', lanchoneteOnly: true },
    { text: 'Monitoramento WhatsApp', icon: <WhatsAppIcon />, path: '/whatsapp-monitoring', adminOnly: true },
    { text: 'Usuários', icon: <PeopleIcon />, path: '/usuarios', adminOrLanchonete: true },
    { text: 'Saques dos Motoboys', icon: <PaidIcon />, path: '/motoboy-payouts', adminOnly: true },
    { text: 'Motoboys', icon: <TwoWheelerIcon />, path: '/motoboys', adminOnly: true },
    { text: 'Meus Motoboys', icon: <TwoWheelerIcon />, path: '/motoboys-management', lanchoneteOnly: true },
    { text: 'Banners', icon: <ImageIcon />, path: '/banners', adminOnly: true },
    { text: 'Aviso de atualização', icon: <SystemUpdateAltIcon />, path: '/android-update', adminOnly: true },
    { text: 'Frete por KM', icon: <TwoWheelerIcon />, path: '/delivery-pricing', adminOnly: true },
    { text: isAdminMaster() ? 'Entregas dos Motoboys' : 'Taxas de Frete Grátis', icon: <TwoWheelerIcon />, path: '/motoboy-deliveries', adminOrLanchonete: true },
    { text: 'Opções de Produtos', icon: <TuneIcon />, path: '/product-options', lanchoneteOnly: true },
    { text: 'Mesas', icon: <RestaurantIcon />, path: '/mesas', lanchoneteOnly: true },
    { text: 'Chat interno', icon: <ChatIcon />, path: '/chat', lanchoneteOnly: true },
  ].filter(item => {
    const staffMenus = {
      cashier: ['Dashboard', 'Pedidos', 'Fechamento de Caixa', 'Mesas', 'Saude do sistema'],
      kitchen: ['Dashboard', 'Pedidos', 'Cozinha', 'Saude do sistema'],
      waiter: ['Mesas']
    };
    if (staffMenus[user?.user_type] && !staffMenus[user.user_type].includes(item.text)) return false;
    if (item.adminOnly && !isAdminMaster()) return false;
    if (item.lanchoneteOnly && !isAdminLanchonete() && !['cashier', 'kitchen', 'waiter'].includes(user?.user_type)) return false;
    if (item.adminOrLanchonete && !isAdminMaster() && !isAdminLanchonete()) return false;
    return true;
  });

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" color="primary" fontWeight="bold">
          Delivery Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === `${adminBasePath}${item.path}` || location.pathname === item.path}
              onClick={() => {
                navigate(`${adminBasePath}${item.path}`);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AutoPrintListener />
      <Dialog
        open={dueAlert.open}
        onClose={() => setDueAlert((current) => ({ ...current, open: false }))}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4, overflow: 'visible' } }}
      >
        <IconButton
          aria-label="Fechar aviso de mensalidade"
          onClick={() => setDueAlert((current) => ({ ...current, open: false }))}
          sx={{ position: 'absolute', right: 10, top: 10, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ textAlign: 'center', px: { xs: 3, sm: 6 }, py: 5 }}>
          <Box sx={{ width: 72, height: 72, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#fff7ed', color: '#ea580c' }}>
            <PaymentIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h4" fontWeight={900} gutterBottom>
            {dueAlert.reason === 'trial-ending' && 'Seu teste gratuito termina hoje'}
            {dueAlert.reason === 'trial-upcoming' && `Seu teste termina em ${dueAlert.days} dia${dueAlert.days === 1 ? '' : 's'}`}
            {dueAlert.reason === 'due' && 'Mensalidade vence hoje'}
            {dueAlert.reason === 'upcoming' && `Mensalidade vence em ${dueAlert.days} dia${dueAlert.days === 1 ? '' : 's'}`}
            {dueAlert.reason === 'overdue' && 'Mensalidade vencida'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            {dueAlert.reason.startsWith('trial-') ? (
              <>Olá, {user?.name || 'lojista'}! O período gratuito da <strong>{dueAlert.store?.name || 'sua lanchonete'}</strong> está perto do fim. O pagamento está previsto para {formatDueDate(dueAlert.dueDate)}.</>
            ) : (
              <>Olá, {user?.name || 'lojista'}! A mensalidade da <strong>{dueAlert.store?.name || 'sua lanchonete'}</strong> vence em {formatDueDate(dueAlert.dueDate)}.</>
            )}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Faça o pagamento para manter todos os recursos e o recebimento de pedidos funcionando normalmente.
          </Typography>
          <Button
            size="large"
            fullWidth
            variant="contained"
            color="warning"
            startIcon={<PaymentIcon />}
            onClick={() => {
              setDueAlert((current) => ({ ...current, open: false }));
              setSubscriptionPaymentOpen(true);
            }}
            sx={{ py: 1.4, fontWeight: 900 }}
          >
            Pagar mensalidade agora
          </Button>
        </DialogContent>
      </Dialog>

      <SubscriptionPaymentDialog
        open={subscriptionPaymentOpen}
        onClose={() => setSubscriptionPaymentOpen(false)}
        lanchoneteId={user?.lanchonete_id}
        lanchoneteName={dueAlert.store?.name}
        amount={59.9}
        onSuccess={() => {
          setSubscriptionPaymentOpen(false);
          setDueAlert((current) => ({ ...current, open: false }));
          window.location.reload();
        }}
      />
      <Dialog
        open={subscriptionBlocked.blocked && !subscriptionPaymentOpen}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogContent sx={{ textAlign: 'center', px: { xs: 3, sm: 6 }, py: 5 }}>
          <Box sx={{ width: 76, height: 76, mx: 'auto', mb: 2, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#fee2e2', color: '#dc2626' }}>
            <LockIcon sx={{ fontSize: 42 }} />
          </Box>
          <Typography variant="h4" fontWeight={900} gutterBottom>
            Conta restrita
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            A mensalidade da <strong>{subscriptionBlocked.store?.name || 'lanchonete'}</strong> esta pendente.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Enquanto o pagamento nao for confirmado, pedidos de garcom e as demais funcoes operacionais ficam bloqueados.
          </Typography>
          {isAdminLanchonete() ? (
            <Button
              size="large"
              fullWidth
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setSubscriptionPaymentOpen(true)}
              sx={{ py: 1.4, fontWeight: 900 }}
            >
              Pagar e liberar acesso
            </Button>
          ) : (
            <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
              Solicite ao administrador da lanchonete que regularize a mensalidade.
            </Alert>
          )}
          <Button onClick={handleLogout} sx={{ mt: 1.5 }}>
            Sair da conta
          </Button>
        </DialogContent>
      </Dialog>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => location.pathname === `${adminBasePath}${item.path}` || location.pathname === item.path)?.text || 'Delivery Admin'}
          </Typography>
          
          <WebPushButton user={user} />

          <IconButton
            color="inherit"
            onClick={handleProfileMenuOpen}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#FFFFFF', color: 'primary.main', fontWeight: 900 }}>
              {user?.name?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <Avatar /> {user?.name}
            </MenuItem>
            <Divider />
            <MenuItem>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Perfil
            </MenuItem>
            {isAdminLanchonete() && (
              <MenuItem onClick={handleOpenPasswordDialog}>
                <ListItemIcon>
                  <LockIcon fontSize="small" />
                </ListItemIcon>
                Alterar senha
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Sair
            </MenuItem>
          </Menu>

          <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>Alterar senha</DialogTitle>
            <DialogContent>
              {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
              <TextField
                autoFocus
                fullWidth
                margin="normal"
                label="Senha atual"
                type="password"
                value={passwords.currentPassword}
                onChange={(event) => setPasswords({ ...passwords, currentPassword: event.target.value })}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Nova senha"
                type="password"
                value={passwords.newPassword}
                onChange={(event) => setPasswords({ ...passwords, newPassword: event.target.value })}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Confirmar nova senha"
                type="password"
                value={passwords.confirmPassword}
                onChange={(event) => setPasswords({ ...passwords, confirmPassword: event.target.value })}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPasswordDialogOpen(false)}>Cancelar</Button>
              <Button variant="contained" onClick={handleChangePassword}>Salvar</Button>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRightColor: '#FECACA' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRightColor: '#FECACA' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}

export default Layout;
